import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { useAuth } from 'src/contexts/auth-context';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Home',
    icon: 'eva:home-fill',
  },
  {
    label: 'Profile',
    icon: 'eva:person-fill',
  },
  {
    label: 'Settings',
    icon: 'eva:settings-2-fill',
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            background: (theme) => alpha(theme.palette.primary.main, 0.12),
            transform: 'scale(1.05)',
          },
          ...(open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: (theme) => `0 0 12px ${alpha(theme.palette.primary.main, 0.4)}`,
          }),
        }}
      >
        <Avatar
          src={user?.user_metadata?.avatar_url}
          alt={user?.user_metadata?.full_name}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
            boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {user?.email?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1,
            ml: 0.75,
            width: 200,
            borderRadius: 1.5,
            boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.dark, 0.15)}`,
            backdropFilter: 'blur(8px)',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            },
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant="subtitle2" noWrap fontWeight="bold" color="primary">
            {user?.user_metadata?.full_name || user?.email}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {MENU_OPTIONS.map((option) => (
          <MenuItem 
            key={option.label} 
            onClick={handleClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1.2,
              px: 2.5,
              transition: (theme) => theme.transitions.create(['background-color']),
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Iconify 
              icon={option.icon} 
              sx={{ 
                mr: 1, 
                color: 'primary.main',
                width: 20,
                height: 20,
              }} 
            />
            {option.label}
          </MenuItem>
        ))}

        <Divider sx={{ borderStyle: 'dashed', m: 0 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ 
            typography: 'body2', 
            color: 'error.main', 
            py: 1.5,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            transition: (theme) => theme.transitions.create(['background-color']),
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <Iconify 
            icon="solar:logout-2-bold" 
            sx={{ 
              mr: 1, 
              color: 'error.main',
              width: 20,
              height: 20,
            }} 
          />
          Logout
        </MenuItem>
      </Popover>
    </>
  );
}
